/*
 * Reading the model as chat sees it. Two things happen in this file and nowhere else.
 *
 * One: core's model types are the structural subset every module shares (core/store/model.ts) —
 * chat writes the full wire rows into `conversations` and `messages` and reads them back as such,
 * and the cast below is where that is said.
 *
 * Two: a selector is shaped to keep object identity. A component subscribes to the slice it needs,
 * never to a freshly built object, because zustand compares with `Object.is`.
 */

import type { ConversationId, ConversationSlice, Model, UserId } from "../../../core/store/model.ts"
import {
	KindDm,
	KindGroupDm,
	NotifyAll,
	NotifyMention,
	NotifyNone,
	type MemberSelf,
} from "../wire/chat.gen.ts"
import type { ConversationView, MessageView } from "../wire/views.ts"
import { CHAT_SLICE, type ChatSlice, type ThreadWindow } from "./slice.ts"

/** The module's own slice; one shared empty slice until the bootstrap has written the first. */
export const chatSlice = CHAT_SLICE.of

/** The model with the module's slice replaced: the one way a write gets in. */
export const withChatSlice = CHAT_SLICE.with

/**
 * Which conversation a `/c/<slug>` route addresses.
 *
 * Every conversation the model knows, not the reader's own list: a public channel they have left or
 * are merely reading is addressable and readable, and it is deliberately absent from
 * `conversationOrder`, which is what the sidebar, the quick switcher and the landing redirect read.
 */
export function conversationIdBySlug(model: Model, ref: string): ConversationId | undefined {
	for (const [id, slice] of Object.entries(model.conversations)) {
		if (slice.meta.slug === ref) return Number(id)
	}
	// A group direct conversation has no slug, and a notification mail therefore addresses it by
	// its id — "g12" (`chat/unread/describer.go`): the mail is written once for a message while the
	// handle form ("anna+bert") differs per reader. A slug wins over this shape, so a channel
	// somebody called "g12" is still that channel.
	const byId = /^g([0-9]+)$/.exec(ref)
	if (byId === null) return undefined
	const id = Number(byId[1])
	return model.conversations[id] === undefined ? undefined : id
}

/**
 * The head of a conversation, wherever this reader holds it: the model's row when the conversation is
 * readable, else the administrator's outside view of it (`ChatSlice.outside`). About, its members and
 * its groups are drawn from this, so the same column serves a conversation one is in and one an
 * administrator manages from outside. Both answers are stored objects, so the identity holds.
 */
export function conversationMeta(model: Model, conv: ConversationId): ConversationView | undefined {
	const meta = model.conversations[conv]?.meta as ConversationView | undefined
	return meta ?? chatSlice(model).outside[conv]
}

/**
 * Which outside view a `/c/<slug>` route addresses, for an administrator who is not in it. Asked only
 * after `conversationIdBySlug` found nothing: a conversation the reader may read always wins.
 */
export function outsideIdBySlug(model: Model, slug: string): ConversationId | undefined {
	for (const [id, view] of Object.entries(chatSlice(model).outside)) {
		if (view.slug === slug) return Number(id)
	}
	return undefined
}

/**
 * The people a `/d/` address names, as ids, or an empty list when the model does not know one of
 * the handles. A direct address is the other people's handles joined by "+" ("anna+bert"), which
 * is what makes it addressable before the conversation exists at all.
 */
export function userIdsOfDmHandle(model: Model, handle: string): UserId[] {
	const wanted = handle.split("+").filter((part) => part !== "")
	if (wanted.length === 0) return []
	const ids: UserId[] = []
	for (const part of wanted) {
		const found = Object.values(model.users).find((user) => user?.handle === part)
		if (found === undefined) return []
		ids.push(found.id)
	}
	return ids
}

/** A dm and a group dm are addressed by their generated handle ("anna+bert"), not by a slug. */
export function conversationIdByDmHandle(model: Model, handle: string): ConversationId | undefined {
	for (const [id, slice] of Object.entries(model.conversations)) {
		const meta = slice.meta as ConversationView
		if (meta.dmHandle === handle) return Number(id)
	}
	return undefined
}

/**
 * Where this reader's read cursor stands in a conversation: the live cursor when one has arrived,
 * else the membership row's, else nothing read.
 */
export function lastReadSeqOf(model: Model, conv: ConversationId): number {
	const slice = chatSlice(model)
	return slice.cursors[conv]?.lastReadSeq ?? slice.self[conv]?.lastReadSeq ?? 0
}

/** The loaded thread of one root, or nothing while no pane has asked for it. */
export function threadOf(model: Model, rootId: number): ThreadWindow | undefined {
	return chatSlice(model).threads[rootId]
}

/**
 * Whether a root's newest live reply is newer than what this reader has read in the conversation.
 * It is the round's only in-conversation signal that a thread moved, and deliberately a heuristic
 * (UX §4.1 R3): replies are outside the read cursor, so the dot clears as soon as a later message
 * of the conversation itself is read. The per-thread watermark is the upgrade.
 *
 * The reader's own replies are the one thing the cursor cannot cover, and they are the ones that
 * would light the dot most often — so the slice remembers them (`threadSeen`) and the heuristic
 * takes the later of the two marks.
 */
export function threadUnread(model: Model, conv: ConversationId, msg: MessageView): boolean {
	const lastReply = msg.lastReplySeq ?? 0
	if (lastReply === 0) return false
	return lastReply > threadReadSeq(model, conv, msg.id)
}

/**
 * Where a reader stands in one thread, as a single seq: the later of the conversation's read cursor
 * and the last reply they have seen in this root. A thread has no cursor of its own, so that pair *is*
 * the watermark — and both readers of it, the footer's dot above and the pane's *New replies* rule,
 * ask here instead of spelling the same `Math.max` twice.
 */
export function threadReadSeq(model: Model, conv: ConversationId, rootId: number): number {
	return Math.max(lastReadSeqOf(model, conv), chatSlice(model).threadSeen[rootId] ?? 0)
}

// ---- the sidebar's order ----

/**
 * A direct conversation is addressed by a handle and never by a slug, and two of the four kinds are
 * one. Spelling the pair once is what keeps the sidebar, the switcher and the order below agreeing
 * about which half of the list a conversation belongs in.
 */
export function isDirect(kind: string): boolean {
	return kind === KindDm || kind === KindGroupDm
}

/**
 * Whether the mute is on: `mutedUntil` names a moment that has not passed yet. It is the state of the
 * row menu's own *Mute* / *Unmute* entry and of nothing else — the notification level is the other,
 * independent axis, and conflating the two would make one control lie about the other.
 *
 * Callers hand it the clock rather than reading one, because the two readers read two clocks: a row
 * follows `useNow()` and stops looking muted on the minute, while the order below takes the time it is
 * recomputed at, which is when an event arrives — a list that re-ordered itself under a reader's hand
 * would be worse than a row that waits.
 */
export function isMuted(self: MemberSelf | undefined, now: number): boolean {
	return (self?.mutedUntil ?? 0) > now
}

/**
 * The level a conversation of this kind hands a new member (`service_membership.go`'s
 * `addMemberRows`, `model.go`): a room speaks up only when the reader is named, a direct
 * conversation speaks up for everything. The rule lives here because two screens need it and each
 * would otherwise guess: the notification settings list only the levels somebody *chose*, and their
 * *Put it back* has to write the level the conversation was given rather than "every message".
 *
 * A conversation the model cannot name is read as a room — the level three of the four kinds carry,
 * and a conversation whose row is gone is one this person has left, where no level matters.
 */
export function defaultNotifyLevel(model: Model, conv: ConversationId): string {
	const meta = model.conversations[conv]?.meta as ConversationView | undefined
	return meta !== undefined && isDirect(meta.kind) ? NotifyAll : NotifyMention
}

/**
 * Whether a conversation is silent for this reader — the server's own rule, `memberRow.silenced`
 * (`model.go`): muted right now, or switched off for good. It is what the sidebar draws grey, sorts
 * last and shows no unread badge for, because those three are one fact: this conversation was asked
 * to keep quiet.
 */
export function isSilenced(self: MemberSelf | undefined, now: number): boolean {
	if (self === undefined) return false
	return self.notifyLevel === NotifyNone || isMuted(self, now)
}

/**
 * What a silent row is still allowed to claim: the mentions alone. It keeps counting unread messages
 * — it is not archived — and says nothing about them; being named in one is the exception a mute was
 * never about (Slack's rule, X-A4 N3).
 */
export function badgeCountOf(slice: ConversationSlice | undefined, silenced: boolean): number {
	if (slice === undefined) return 0
	return silenced ? slice.mentions : slice.unread
}

/** One conversation's place in the order, with the facts the comparison reads. */
interface Place {
	id: ConversationId
	starred: number
	direct: number
	silenced: number
	/** A channel sorts by the address it shows, which is its slug with a hash in front of it. */
	slug: string
	/** A direct conversation sorts by its last message, newest first. */
	lastMessageAt: number
	/** The server's own order, as the tiebreaker that keeps the list stable. */
	index: number
}

function placeOf(
	model: Model,
	slice: ChatSlice,
	id: ConversationId,
	index: number,
	now: number,
): Place | undefined {
	const meta = model.conversations[id]?.meta as ConversationView | undefined
	const self = slice.self[id]
	// The membership gate (A-01 §4.2) and the closed conversation: the model holds a row for every
	// conversation this account may read, and the order is only about the ones in their own list.
	if (meta === undefined || self === undefined || self.state !== "active" || self.hidden) {
		return undefined
	}
	return {
		id,
		starred: self.starred ? 0 : 1,
		direct: isDirect(meta.kind) ? 1 : 0,
		silenced: isSilenced(self, now) ? 1 : 0,
		slug: meta.slug ?? "",
		lastMessageAt: meta.lastMessageAt ?? 0,
		index,
	}
}

function comparePlaces(a: Place, b: Place): number {
	if (a.starred !== b.starred) return a.starred - b.starred
	if (a.direct !== b.direct) return a.direct - b.direct
	if (a.silenced !== b.silenced) return a.silenced - b.silenced
	if (a.direct === 1) {
		if (a.lastMessageAt !== b.lastMessageAt) return b.lastMessageAt - a.lastMessageAt
	} else if (a.slug !== b.slug) {
		return a.slug.localeCompare(b.slug, "en")
	}
	return a.index - b.index
}

/** The one memo that keeps `sidebarOrder` subscribable; every key of it is replaced by a reducer. */
let orderCache:
	| {
			conversations: Model["conversations"]
			self: ChatSlice["self"]
			order: Model["conversationOrder"]
			result: ConversationId[]
	  }
	| undefined

/**
 * The one order the sidebar, `Alt+↑`/`Alt+↓` and the quick switcher read (X-A4 N3): starred first,
 * then channels by the address they show, then direct conversations by their last message. Inside
 * each of those a muted conversation comes last, and one this person has closed is not in the list
 * at all.
 *
 * It is a selector and not a sorted array in the model because every input arrives as an event — a
 * star, a mute, a new message — and `bootstrap.ts`'s cold-start sort could only ever answer for the
 * first frame (X-A4 N3, N18.4). The result keeps its identity while the three facts it reads keep
 * theirs, so a component may subscribe to it: zustand compares with `Object.is`, and a selector that
 * built a fresh array every render would re-render for ever.
 */
export function sidebarOrder(model: Model): ConversationId[] {
	const slice = chatSlice(model)
	if (
		orderCache !== undefined &&
		orderCache.conversations === model.conversations &&
		orderCache.self === slice.self &&
		orderCache.order === model.conversationOrder
	) {
		return orderCache.result
	}
	const now = Date.now()
	const places: Place[] = []
	model.conversationOrder.forEach((id, index) => {
		const place = placeOf(model, slice, id, index, now)
		if (place !== undefined) places.push(place)
	})
	places.sort(comparePlaces)
	const sorted = places.map((place) => place.id)
	// `model.conversations` is replaced by every event in every conversation, and almost none of them
	// move this order: what it reads is a star, a mute, a slug and a direct conversation's last
	// message. So the answer that comes out is compared with the one before it, and the *same array*
	// is handed back when they agree — every subscriber to this selector compares with `Object.is`,
	// so a fresh array of the same ids re-rendered the whole sidebar on every arriving message.
	const held = orderCache?.result
	const result =
		held !== undefined && held.length === sorted.length && held.every((id, at) => id === sorted[at])
			? held
			: sorted
	orderCache = {
		conversations: model.conversations,
		self: slice.self,
		order: model.conversationOrder,
		result,
	}
	return result
}

/**
 * The conversation `Alt+↑`/`Alt+↓` moves to, and with `unreadOnly` the one `Alt+Shift+↑`/`↓` does.
 * The walk is `sidebarOrder` and it wraps, so the last row's successor is the first: a shortcut that
 * stopped at the end would leave a reader pressing it twice to find out that it had.
 *
 * "Unread" here is what the row's own badge shows — the mentions alone in a muted conversation
 * (`badgeCountOf`). A jump to a row that says nothing is a jump a reader cannot account for.
 */
export function nextConversation(
	model: Model,
	from: ConversationId | undefined,
	dir: 1 | -1,
	unreadOnly: boolean,
): ConversationId | undefined {
	const order = sidebarOrder(model)
	if (order.length === 0) return undefined
	const at = from === undefined ? -1 : order.indexOf(from)
	// From outside the list the first step lands on the first row of the direction it walks in.
	const start = at === -1 ? (dir === 1 ? -1 : order.length) : at
	const slice = chatSlice(model)
	const now = Date.now()
	for (let step = 1; step <= order.length; step++) {
		const index = (((start + dir * step) % order.length) + order.length) % order.length
		const id = order[index]
		if (id === undefined || id === from) continue
		if (!unreadOnly) return id
		if (badgeCountOf(model.conversations[id], isSilenced(slice.self[id], now)) > 0) return id
	}
	return undefined
}
